import { conservationCards, conservationIntro } from "@/mocks/home";
import { useReveal } from "@/hooks/useReveal";

const BANNER =
  "https://images.squarespace-cdn.com/content/v1/55db92c4e4b035628e290c2f/1440627904575-CW2S0377RB038YE8IQUP/earthgrass.jpg?format=2500w";

export default function Conservation() {
  const intro = useReveal<HTMLDivElement>();
  const cards = useReveal<HTMLDivElement>();

  return (
    <section id="conservation" className="w-full bg-background-50">
      {/* Banner */}
      <div className="relative w-full h-[280px] md:h-[420px] overflow-hidden">
        <img
          src={BANNER}
          alt="Green grass and earth"
          title="Conservation"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/45" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <p className="text-[11px] uppercase tracking-[0.32em] text-white/80">Our Efforts Locally &amp; Nationally</p>
          <h2 className="mt-4 font-heading font-light text-3xl md:text-5xl text-white">Conservation</h2>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-20 md:py-28">
        <div ref={intro.ref} className={`reveal ${intro.visible ? "is-visible" : ""} max-w-4xl mx-auto`}>
          <h3 className="font-heading text-2xl md:text-3xl text-foreground-950">
            National Affairs &amp; Legislation (NAL) and Conservation Committees
          </h3>
          <div className="mt-7 space-y-5">
            {conservationIntro.map((p, i) => (
              <p key={i} className="text-[15px] leading-relaxed text-foreground-700">
                {p}
              </p>
            ))}
          </div>

          <div className="mt-10 rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-8 flex items-start gap-4">
            <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-file-text-line text-xl" aria-hidden="true" />
            </span>
            <div>
              <h4 className="font-heading text-lg text-foreground-950">GCA Position Papers</h4>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                The focal issues for the NAL Committee are explicitly detailed in the GCA Position Papers, created
                by both committees and approved by the Executive committee.
              </p>
              <a
                href="https://www.gcamerica.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
              >
                Explore the GCA
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>

        {/* Programs */}
        <div ref={cards.ref} className={`reveal ${cards.visible ? "is-visible" : ""} max-w-6xl mx-auto mt-16 md:mt-20`}>
          <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center">
            Programs We Champion
          </h3>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {conservationCards.map((card) => (
              <article
                key={card.title}
                className="group rounded-lg overflow-hidden border border-background-200/80 bg-background-50 flex flex-col"
              >
                <div className="w-full h-[180px] overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.imageAlt}
                    title={card.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h4 className="font-heading text-lg text-foreground-950">{card.title}</h4>
                  <p className="mt-2.5 text-sm leading-relaxed text-foreground-600 flex-1">{card.body}</p>
                  <a
                    href={card.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    {card.hrefLabel}
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}